import React, { useState, useEffect } from 'react';
import { isEarlyBirdActive, getTimeRemaining, fetchEarlyBirdConfig } from '../config/earlyBirdEvent';
import { useAuth } from '../context/AuthContext';

const BANNER_DISMISSED_KEY = 'earlyBirdBannerDismissed';
const AUTO_HIDE_DELAY = 10000; // 10 seconds for logged-in users

const EarlyBirdBanner: React.FC = () => {
  const { currentUser } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [, setConfigLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Fetch config from server on mount
  useEffect(() => {
    fetchEarlyBirdConfig().then(() => {
      setConfigLoaded(true);
      // Check if banner should be visible after config loads
      const dismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
      setIsVisible(isEarlyBirdActive() && !dismissed);
    });
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    document.body.style.paddingTop = '0';
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide for logged-in users after 10 seconds
  useEffect(() => {
    if (!isVisible || !currentUser) return;

    const autoHideTimer = setTimeout(() => {
      setIsVisible(false);
      document.body.style.paddingTop = '0';
    }, AUTO_HIDE_DELAY);

    return () => clearTimeout(autoHideTimer);
  }, [isVisible, currentUser]);

  useEffect(() => {
    if (!isVisible) {
      document.body.style.paddingTop = '0';
      return;
    }

    // Add padding to body to prevent content from being hidden behind banner
    document.body.style.paddingTop = isMobile ? '80px' : '52px';

    const timer = setInterval(() => {
      const remaining = getTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining.total <= 0) {
        setIsVisible(false);
        document.body.style.paddingTop = '0';
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      document.body.style.paddingTop = '0';
    };
  }, [isVisible, isMobile]);

  if (!isVisible) return null;

  const { days, hours, minutes, seconds } = timeRemaining;

  return (
    <div style={styles.banner}>
      {/* Close button - top right on mobile */}
      <button
        onClick={handleClose}
        style={{
          ...styles.closeButton,
          ...(isMobile ? styles.closeButtonMobile : {}),
        }}
        aria-label="Close banner"
      >
        ✕
      </button>
      <div style={{
        ...styles.content,
        ...(isMobile ? styles.contentMobile : {}),
      }}>
        {!isMobile && <span style={styles.icon}>🎉</span>}
        <span style={styles.text}>
          <strong>Early Bird Event!</strong> All features unlocked for FREE users
        </span>
        <div style={styles.countdown}>
          <span style={styles.countdownLabel}>Ends in:</span>
          <div style={styles.timerWrapper}>
            {days > 0 && (
              <div style={styles.timeBlock}>
                <span style={styles.timeValue}>{days}</span>
                <span style={styles.timeUnit}>d</span>
              </div>
            )}
            <div style={styles.timeBlock}>
              <span style={styles.timeValue}>{String(hours).padStart(2, '0')}</span>
              <span style={styles.timeUnit}>h</span>
            </div>
            <div style={styles.timeBlock}>
              <span style={styles.timeValue}>{String(minutes).padStart(2, '0')}</span>
              <span style={styles.timeUnit}>m</span>
            </div>
            <div style={styles.timeBlock}>
              <span style={styles.timeValue}>{String(seconds).padStart(2, '0')}</span>
              <span style={styles.timeUnit}>s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  banner: {
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '12px 20px',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  contentMobile: {
    paddingTop: '28px',
    paddingRight: '8px',
    paddingLeft: '8px',
  },
  icon: {
    fontSize: '24px',
  },
  text: {
    fontSize: '15px',
    letterSpacing: '0.3px',
  },
  countdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  countdownLabel: {
    fontSize: '13px',
    opacity: 0.9,
  },
  timerWrapper: {
    display: 'flex',
    gap: '4px',
  },
  timeBlock: {
    display: 'flex',
    alignItems: 'baseline',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '4px',
    padding: '4px 8px',
  },
  timeValue: {
    fontSize: '18px',
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  timeUnit: {
    fontSize: '12px',
    marginLeft: '2px',
    opacity: 0.8,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background 0.2s',
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  closeButtonMobile: {
    top: '8px',
    right: '8px',
    transform: 'none',
    padding: '6px 10px',
    fontSize: '18px',
  },
};

export default EarlyBirdBanner;
